import {call, put, takeLatest} from '@redux-saga/core/effects';
import {push} from 'connected-react-router';
import {Async, AsyncError, isLoaded, Loaded, Loading} from 'Core/models/Async';
import {UnvalidatedEmail, ValidatableEmail, ValidEmail} from 'Core/models/Email';
import {EmptyString, NonEmptyString} from 'Core/models/EmptyString';
import {ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {EmptyPhoneNumber, ValidatablePhone, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import {Either, fold as foldEither, left, right} from 'fp-ts/lib/Either';
import {fold as foldOption, none, Option, some} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {map as mapTaskEither} from 'fp-ts/lib/TaskEither';
import {Lens} from 'monocle-ts';
import {createAction} from 'Shared/utilities/actionCreator';
import {eq} from 'Shared/utilities/equals';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {getAgentModelClaims} from 'Users/models/Claims';
import {ErrorVip, UnvalidatedVip, validateVip, ValidVip, Vip} from 'Users/models/Vip';
import {UserRouteConstants} from 'Users/Routes';

export enum VipUserActionType {
  CHANGE_USER = 'vipUser/CHANGE',
  LOAD_EXISTING_USER = 'vipUser/LOAD_EXISTING_USER',
  SAVE_VIP_USER = 'vipUser/SAVE_VIP_USER',
  FETCH_VIP_USER = 'vipUser/FETCH_VIP_USER',
  VALIDATE_USERNAME = 'vipUser/VALIDATE_USERNAME',
  SET_USERNAME = 'vipUser/SET_USERNAME',
  VALIDATE_PHONE = 'vipUser/VALIDATE_PHONE',
  SET_PHONE = 'vipUser/SET_PHONE',
  VALIDATE_EMAIL = 'vipUser/VALIDATE_EMAIL',
  SET_EMAIL = 'vipUser/SET_EMAIL',
}

export const validatePhone = (phone: string) => createAction(VipUserActionType.VALIDATE_PHONE, phone);
export const setPhone = (phone: ValidatablePhone) => createAction(VipUserActionType.SET_PHONE, phone);
export const validateEmail = (email: string) => createAction(VipUserActionType.VALIDATE_EMAIL, email);
export const setEmail = (email: ValidatableEmail) => createAction(VipUserActionType.SET_EMAIL, email);
export const validateUsername = (username: string) => createAction(VipUserActionType.VALIDATE_USERNAME, username);
export const setUsername = (username: ValidatableNonEmptyString) =>
  createAction(VipUserActionType.SET_USERNAME, username);
export const getVipUser = (userId: string) => createAction(VipUserActionType.FETCH_VIP_USER, userId);
export const loadExistingVip = (vip: Async<Vip>) => createAction(VipUserActionType.LOAD_EXISTING_USER, vip);
export const changeVipUser = (vip: Async<Vip>) => createAction(VipUserActionType.CHANGE_USER, vip);
export const clearVipUser = () => createAction(VipUserActionType.CHANGE_USER, DEFAULT_STATE.vip);
export const saveVipUser = (vip: ValidVip, id?: string) => createAction(VipUserActionType.SAVE_VIP_USER, {vip, id});

type VipUserState = {
  vip: Async<Vip>;
  originalVip: Option<Async<Vip>>;
};

export const DEFAULT_STATE = {
  originalVip: none,
  vip: new Loaded(
    new UnvalidatedVip(
      new EmptyString(),
      new EmptyString(),
      new Loaded(left(new EmptyString())),
      new Loaded(left(new EmptyPhoneNumber())),
      new Loaded(left(new UnvalidatedEmail(''))),
      false,
      false,
      false
    )
  ),
};

type VipUserAction =
  | ReturnType<typeof setUsername>
  | ReturnType<typeof setPhone>
  | ReturnType<typeof setEmail>
  | ReturnType<typeof changeVipUser>
  | ReturnType<typeof loadExistingVip>;

const vipLens = Lens.fromProp<Vip>();

export default (state: VipUserState = DEFAULT_STATE, action: VipUserAction): VipUserState => {
  switch (action.type) {
    case VipUserActionType.SET_EMAIL: {
      return {
        ...state,
        vip: isLoaded(state.vip)
          ? new Loaded(validateVip(vipLens('email').set(action.payload)(state.vip.Data)))
          : state.vip,
      };
    }
    case VipUserActionType.SET_USERNAME: {
      return {
        ...state,
        vip: isLoaded(state.vip)
          ? new Loaded(validateVip(vipLens('username').set(action.payload)(state.vip.Data)))
          : state.vip,
      };
    }
    case VipUserActionType.SET_PHONE: {
      return {
        ...state,
        vip: isLoaded(state.vip)
          ? new Loaded(validateVip(vipLens('phoneNumber').set(action.payload)(state.vip.Data)))
          : state.vip,
      };
    }
    case VipUserActionType.LOAD_EXISTING_USER:
      return {
        ...state,
        originalVip: some(action.payload),
        vip: action.payload,
      };
    case VipUserActionType.CHANGE_USER:
      return {
        ...state,
        vip: action.payload,
      };
    default:
      return state;
  }
};

const getVipUserFromApi = (action: ReturnType<typeof getVipUser>) =>
  pipe(
    () => userApi.getUser(action.payload),
    mapTaskEither(user =>
      ValidVip.create(
        NonEmptyString.create(user.firstName),
        NonEmptyString.create(user.lastName),
        new Loaded(right(NonEmptyString.create(user.userName) as NonEmptyString)),
        user.mobilePhone
          ? new Loaded(right(ValidPhoneNumber.create(user.mobilePhone) as ValidPhoneNumber))
          : new Loaded(left(new EmptyPhoneNumber())),
        new Loaded(right(ValidEmail.create(user.email) as ValidEmail)),
        user.isAhl || false,
        user.isNewYork || false,
        pipe(
          getAgentModelClaims(user.userTypes),
          foldOption(
            () => false,
            claims => claims.roles.some(eq('id', RoleId.AllstateExternalAdministrator))
          )
        )
      )
    )
  )();

export function* getVipUserSaga() {
  yield takeLatest(VipUserActionType.FETCH_VIP_USER, getApiCallGenerator(getVipUserFromApi, loadExistingVip));
}

export function* saveVipUserGenerator(action: ReturnType<typeof saveVipUser>) {
  yield put(changeVipUser(new Loading()));

  const vipUserResult: Either<any, any> = yield call(userApi.saveVipUser, action.payload.vip, action.payload.id);

  function* onSaveVip() {
    yield put(push(UserRouteConstants.USERS));
  }

  function* onVipError(errorDetails: AsyncError) {
    const errorResult = new ErrorVip(
      action.payload.vip.firstName,
      action.payload.vip.lastName,
      action.payload.vip.username,
      action.payload.vip.phoneNumber,
      action.payload.vip.email,
      errorDetails,
      action.payload.vip.isAhl || false,
      action.payload.vip.isNewYork || false,
      action.payload.vip.isExternalAdministrator || false
    );

    yield put(changeVipUser(new Loaded(errorResult)));
  }

  yield pipe(vipUserResult, foldEither(onVipError, onSaveVip as any));
}

export function* saveVipUserSaga() {}
